import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import API from '../../services/api';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import { Field, Row, Notice, StatusBadge, inputStyle, selectStyle, apiErrors } from '../insurance/formBits';
import { formatMoney } from '../../insurance/format';
import { formatLongDate } from '../../dateOnly';
import { actionsFor, submitOutcomeText } from '../../insurance/claims';
import ClaimStatusBadge from './ClaimStatusBadge';
import ClaimTracker from './ClaimTracker';
import ClaimTimeline from './ClaimTimeline';
import ValidationPanel from './ValidationPanel';
import ClaimLinesEditor from './ClaimLinesEditor';

// One claim, for either role. `role` decides which buttons are offered; the API
// decides what is allowed (404 for a claim that is not yours, 409 for a bad move,
// 403 for edits the role may not make). Draft edits are buffered locally and sent
// with one PUT, so a 400 keeps what was typed and shows the field errors.
const POS = [
  ['11', '11 — Office'], ['10', '10 — Telehealth, client at home'], ['02', '02 — Telehealth, other location'],
  ['12', '12 — Client home'], ['03', '03 — School'], ['53', '53 — Community mental health center'],
];
const ATTEST_TEXT = 'Progress note is complete and signed for this session.';
const h3 = { margin: '0 0 8px', fontSize: 15, color: 'var(--primary-color)' };
const muted = { fontSize: 13, color: 'var(--text-muted)' };

export default function ClaimDetailView({ claimId, role, backTo }) {
  const isAdmin = role === 'admin';
  const [d, setD] = useState(null);
  const [state, setState] = useState('loading');     // loading | ok | unavailable | error
  const [error, setError] = useState('');
  const [msg, setMsg] = useState(null);                // { tone, text }
  const [draft, setDraft] = useState(null);            // buffered edits (PUT field names); null = clean
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  // The line editor's text inputs are uncontrolled, so whenever the server's copy
  // replaces what is on screen (reload, Discard) the editor is remounted to re-seed them.
  const [editorKey, setEditorKey] = useState(0);
  const reseedEditor = () => setEditorKey((k) => k + 1);

  const load = useCallback(async () => {
    try { const r = await API.get(`/billing/claims/${claimId}`); setD(r.data); setEditorKey((k) => k + 1); setState('ok'); }
    catch (err) {
      if (err.response && err.response.status === 404) setState('unavailable');
      else { setError(apiErrors(err).message); setState('error'); }
    }
  }, [claimId]);
  useEffect(() => { load(); }, [load]);

  if (state === 'loading') return <div style={{ padding: 24, color: 'var(--text-muted)' }}>Loading claim…</div>;
  if (state === 'unavailable') return <div style={{ padding: 24, fontSize: 14, color: 'var(--text-muted)' }}>This claim is not available. <Link to={backTo}>Back</Link></div>;
  if (state === 'error') return <div style={{ padding: 24 }}><Notice tone="bad">{error}</Notice></div>;

  const { claim, lines, events, submissions, validation, nextAction, tracker, attention, policy, enrollment, canEdit, showAmounts } = d;
  const acts = actionsFor(role, claim, validation && validation.ok);
  const editing = Boolean(canEdit && acts.edit);
  const edited = draft !== null;
  const pick = (key, fallback) => (edited && draft[key] !== undefined ? draft[key] : fallback);
  const view = {   // what the editor shows = server data + buffered patches
    placeOfService: pick('placeOfService', claim.place_of_service),
    telehealth: pick('telehealth', claim.telehealth),
    priorAuthNumber: pick('priorAuthNumber', claim.prior_auth_number || ''),
    internalNotes: pick('internalNotes', claim.internal_notes || ''),
    lines: lines.map((l) => {
      const p = edited && draft.lines && draft.lines[l.id];
      if (!p) return l;
      return {
        ...l,
        cpt_code: p.cptCode !== undefined ? p.cptCode : l.cpt_code,
        modifiers: p.modifiers !== undefined ? p.modifiers : l.modifiers,
        units: p.units !== undefined ? p.units : l.units,
        charge: p.charge !== undefined ? p.charge : l.charge,
        diagnosis_pointers: p.diagnosisPointers !== undefined ? p.diagnosisPointers : l.diagnosis_pointers,
      };
    }),
  };
  const patch = (fields) => { setDraft((prev) => ({ ...(prev || {}), ...fields })); setFieldErrors({}); };
  const patchLine = (id, p) => setDraft((prev) => {
    const prevLines = (prev && prev.lines) || {};
    return { ...(prev || {}), lines: { ...prevLines, [id]: { ...(prevLines[id] || {}), ...p } } };
  });

  const post = (path, body) => API.post(`/billing/claims/${claim.id}/${path}`, body);
  async function act(label, fn) {
    setBusy(true); setMsg(null);
    try { await fn(); await load(); }
    catch (err) {
      const e = apiErrors(err);
      setMsg({ tone: 'bad', text: `${label}: ${e.message}` });
      const v = err.response && err.response.data && err.response.data.validation;
      if (v) setD((prev) => ({ ...prev, validation: v }));
    } finally { setBusy(false); }
  }
  async function save() {
    setBusy(true); setMsg(null); setFieldErrors({});
    const { lines: lineMap, ...rest } = draft;
    const body = { ...rest };
    if (lineMap) body.lines = Object.entries(lineMap).map(([id, p]) => ({ id: Number(id), ...p }));
    try { await API.put(`/billing/claims/${claim.id}`, body); setDraft(null); await load(); setMsg({ tone: 'good', text: 'Saved.' }); }
    catch (err) { const e = apiErrors(err); setFieldErrors(e.fieldErrors || {}); setMsg({ tone: 'bad', text: e.message }); }
    finally { setBusy(false); }
  }
  async function submit() {
    setBusy(true); setMsg(null);
    try {
      const r = await API.post('/billing/claims/submit', { claimIds: [claim.id] });
      const res = r.data.results[0];
      setMsg({ tone: res.outcome === 'submitted' ? 'good' : 'bad', text: submitOutcomeText(res) });
      await load();
    } catch (err) { setMsg({ tone: 'bad', text: apiErrors(err).message }); }
    finally { setBusy(false); }
  }
  const posLabel = (code) => (POS.find(([v]) => v === code) || [code, code])[1];

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '20px 16px' }}>
      <div style={{ fontSize: 13, marginBottom: 8 }}><Link to={backTo}>← Claims</Link></div>

      <Card style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ margin: 0, fontSize: 20, color: 'var(--primary-color)' }}>{claim.client_name}</h1>
            <div style={{ ...muted, marginTop: 2 }}>
              {claim.payer_name} · {formatLongDate(claim.dos)} · {claim.therapist_name} · Control # <span>{claim.patient_control_number}</span>
              {showAmounts && claim.total_charge !== null && claim.total_charge !== undefined && <> · Charge {formatMoney(claim.total_charge)}</>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <ClaimStatusBadge status={claim.status} statusLabel={claim.status_label} />
            {attention && attention.flag && <StatusBadge tone="bad">{attention.reason}</StatusBadge>}
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{claim.days_in_status} day{claim.days_in_status === 1 ? '' : 's'} in this status</span>
          </div>
        </div>
        <div style={{ marginTop: 12 }}><ClaimTracker tracker={tracker} /></div>
        <div style={{ marginTop: 12, fontSize: 14 }}><strong>Next:</strong> {nextAction}</div>
      </Card>

      {msg && <div style={{ marginBottom: 12 }}><Notice tone={msg.tone}>{msg.text}</Notice></div>}

      <Card style={{ marginBottom: 12 }}>
        <h3 style={h3}>Checks</h3>
        <ValidationPanel validation={validation} />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10, alignItems: 'center' }}>
          {acts.attest && (
            <label style={{ fontSize: 14, display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" aria-label={ATTEST_TEXT} checked={false} disabled={busy} onChange={() => act('Attest', () => post('attest'))} />
              {ATTEST_TEXT}
            </label>
          )}
          {claim.note_attested_at && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Attested {new Date(claim.note_attested_at).toLocaleString()}</span>}
          {claim.status === 'draft' && <Button variant="primary" disabled={busy || !acts.ready || edited} onClick={() => act('Mark ready', () => post('ready'))}>Mark ready to submit</Button>}
          {acts.unready && <Button variant="subtle" disabled={busy} onClick={() => act('Back to draft', () => post('unready'))}>Back to draft</Button>}
          {acts.submit && <Button variant="success" disabled={busy} onClick={submit}>Submit to payer</Button>}
          {acts.reopen && <Button variant="primary" disabled={busy} onClick={() => act('Reopen', () => post('reopen'))}>Reopen as draft</Button>}
          {acts.sync && <Button variant="ghost" disabled={busy} onClick={() => act('Check status', () => post('sync'))}>Check status with Stedi</Button>}
          {claim.status === 'draft' && <Button variant="ghost" disabled={busy} onClick={() => act('Re-run checks', () => post('validate'))}>Re-run checks</Button>}
          {acts.void && <Button variant="danger" disabled={busy} onClick={() => { if (window.confirm('Void this claim? The session can be claimed again.')) act('Void', () => post('void')); }}>Void</Button>}
        </div>
      </Card>

      <Card style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <h3 style={h3}>Claim</h3>
          {editing && (
            <div style={{ display: 'flex', gap: 8 }}>
              {edited && <Button variant="subtle" disabled={busy} onClick={() => { setDraft(null); setFieldErrors({}); reseedEditor(); }}>Discard</Button>}
              <Button variant="primary" disabled={busy || !edited} onClick={save}>Save changes</Button>
            </div>
          )}
        </div>
        <Row>
          <Field label="Place of service" error={fieldErrors.placeOfService}>
            {editing
              ? (
                <select aria-label="Place of service" style={selectStyle} value={view.placeOfService} onChange={(e) => patch({ placeOfService: e.target.value })}>
                  {POS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  {!POS.some(([v]) => v === view.placeOfService) && <option value={view.placeOfService}>{view.placeOfService}</option>}
                </select>
              )
              : <div style={{ fontSize: 14 }}>{posLabel(claim.place_of_service)}</div>}
          </Field>
          <Field label="Telehealth" hint={editing ? "Switching it on applies the payer's place of service and modifier." : undefined}>
            {editing
              ? <label style={{ fontSize: 14, display: 'flex', gap: 8, alignItems: 'center', minHeight: 36 }}><input type="checkbox" checked={Boolean(view.telehealth)} onChange={(e) => patch({ telehealth: e.target.checked })} /> Telehealth session</label>
              : <div style={{ fontSize: 14 }}>{claim.telehealth ? 'Yes' : 'No'}</div>}
          </Field>
          <Field label="Prior authorization #" error={fieldErrors.priorAuthNumber}>
            {editing
              ? <input aria-label="Prior authorization number" style={inputStyle} value={view.priorAuthNumber} onChange={(e) => patch({ priorAuthNumber: e.target.value })} />
              : <div style={{ fontSize: 14 }}>{claim.prior_auth_number || '—'}</div>}
          </Field>
        </Row>
        <div style={{ ...muted, margin: '8px 0 10px' }}>
          Diagnoses on this claim: {(claim.diagnosis_codes || []).map((c, i) => `${i + 1}: ${c}`).join('  ·  ') || 'none'}.
          {' '}Diagnoses are edited on the client's profile; a draft picks them up when you re-run the checks.
        </div>
        <ClaimLinesEditor key={editorKey} lines={view.lines} diagnosisCodes={claim.diagnosis_codes || []} editable={editing} canEditCodes={isAdmin} showAmounts={showAmounts} onChange={patchLine} errors={fieldErrors} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginTop: 12, fontSize: 13 }}>
          <div>
            <div style={muted}>Policy</div>
            {policy ? <>{policy.payerName} · member {policy.memberId || '—'} · {policy.relationship || 'self'}{policy.authorizationSignedOn ? '' : ' · no signed authorization'}</> : '—'}
          </div>
          <div>
            <div style={muted}>Therapist enrollment</div>
            {enrollment ? `${enrollment.status}${enrollment.effectiveDate ? `, effective ${formatLongDate(enrollment.effectiveDate)}` : ', no effective date yet'}` : 'none'}
          </div>
          {showAmounts && (
            <div>
              <div style={muted}>Paid / patient responsibility</div>
              {formatMoney(claim.total_paid || 0)} / {formatMoney(claim.patient_responsibility || 0)}
            </div>
          )}
          {claim.stedi_status && (
            <div>
              <div style={muted}>Stedi status</div>
              {claim.stedi_status}{claim.stedi_claim_id ? ` · ${claim.stedi_claim_id}` : ''}
            </div>
          )}
        </div>
        {isAdmin && (
          <Field label="Internal notes (office only)" style={{ marginTop: 10 }}>
            {canEdit
              ? <textarea aria-label="Internal notes" style={{ ...inputStyle, minHeight: 60 }} value={view.internalNotes} onChange={(e) => patch({ internalNotes: e.target.value })} />
              : <div style={{ fontSize: 14 }}>{claim.internal_notes || '—'}</div>}
          </Field>
        )}
      </Card>

      <Card>
        <h3 style={h3}>Timeline</h3>
        <form
          onSubmit={(e) => { e.preventDefault(); const text = note.trim(); if (!text) return; act('Add note', async () => { await post('notes', { message: text }); setNote(''); }); }}
          style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}
        >
          <input aria-label="New note" style={{ ...inputStyle, flex: 1, minWidth: 200 }} placeholder="Add a note to the timeline" value={note} onChange={(e) => setNote(e.target.value)} />
          <Button variant="subtle" type="submit" disabled={busy || !note.trim()}>Add note</Button>
        </form>
        <ClaimTimeline events={events} />
        {submissions && submissions.length > 0 && (
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-muted)' }}>
            Submissions: {submissions.map((s) => `v${s.version} ${s.outcome}${s.http_status ? ` (HTTP ${s.http_status})` : ''} ${new Date(s.created_at).toLocaleString()}`).join(' · ')}
          </div>
        )}
      </Card>
    </div>
  );
}
